/**
 * How good a deal is this listing?
 *
 * The quality factor already in the ranking (utils/listing-quality.ts) scores
 * buyability: photos, description, shippable, seller trust. That was the right
 * signal when the corpus was mixed, and it is close to useless now -- 99.98% of
 * active organic listings are shippable, verified sellers upload full photo
 * sets, and a factor almost every document maxes out buys no ordering at all.
 * It measures whether a listing is well made, not whether it is worth buying.
 *
 * This one measures the offer. A buyer choosing between two Bottega Veneta bags
 * cares about one thing the marketplace can know and does not currently use:
 * how far below the going rate this one is priced.
 *
 * SCALE. 0 means "priced at or above what these sell for", not "bad listing" --
 * the ranking factor floors at 1.0, so an unscored or fairly-priced listing is
 * left exactly where it was and only a genuine discount lifts anything. That
 * matters because coverage will be partial for a long time: a listing with no
 * price analysis must never be pushed down by its absence.
 *
 * CONFIDENCE IS NOT COSMETIC. The market estimate comes from a language model
 * reading a title, a description and (where available) photos. On a Rolex it
 * knows the comp cold; on "vintage jar, unmarked" it is guessing. A low-
 * confidence estimate earns a fraction of the uplift a high-confidence one
 * does, so the factor degrades toward neutral rather than inventing bargains.
 */

import { AiPriceAnalysisSchema, type AiPriceAnalysis, type PriceConfidence } from '@/schemas/api/ai/suggest-price';
import { resolveSourceTier } from './elasticsearch/relevance-weights';
import { DATA_SOURCE } from '../data/js/data-sources';

type EstimateSource = NonNullable<AiPriceAnalysis['estimateSource']>;

/**
 * Is a deal score worth computing for this listing?
 *
 * Only for SECOND-HAND inventory we sell ourselves, which is a narrower set
 * than the ranking's `organic` tier and deliberately so.
 *
 * A click-out feed listing earns a CPM when someone leaves the site, so how its
 * price compares to the going rate changes nothing about what it is worth to
 * the marketplace -- and paying a model to estimate 46,000 of them would be
 * spending real money on a signal nothing acts on.
 *
 * CJ DROPSHIPPING IS EXCLUDED TOO, despite being organic for ranking. The
 * ranking asks "do we sell this ourselves", and CJ is imported but fulfilled by
 * us, so `organic` is the right answer there. This asks something else
 * entirely: "what does one of these, used, in this condition, sell for on the
 * secondary market". A dropshipped item has no answer to that. It is new stock
 * at a supplier-set price, there is no wear to judge and no comp to beat, and
 * an estimate against it would be measuring the supplier's margin rather than a
 * bargain. The two questions happen to agree on every other source, which is
 * why one gate served both for a while.
 *
 * The gate lives here, next to the arithmetic, so the indexer, the cron, the
 * tagger and the backfill cannot drift apart on it.
 */
export function dealScoreApplies(dataSourceId: number | null | undefined): boolean {
  if (dataSourceId === DATA_SOURCE.CJ_DROPSHIPPING) return false;
  return resolveSourceTier(dataSourceId) === 'organic';
}

/**
 * Discount at which a listing scores 100.
 *
 * Was 0.5 with full credit at $25 saved, and 178 live listings sat at 100 on
 * prod, 2026-10-10: a $450 saving on a Louis Vuitton wallet tied a $40 one, so
 * the top of the inbox could not be sorted. 70% off and $100 saved took that to
 * 12, and listings at 60 or more from 2,362 to 138.
 */
export const DEEP_DISCOUNT = 0.7;

/**
 * Dollars a buyer must save before a discount earns full marks.
 *
 * A percentage alone made a $6 record "worth $12" the same 50%-off deal as a
 * $400 bag at $200. On prod, 2026-10-07, 41% of scored listings under $10 sat at
 * 70+ against 9% of those over $250, and the cheap ones' references were the
 * model's canned guesses ("$12, band 8-18" for every vinyl LP). Below this many
 * dollars saved, the score shrinks in proportion. It was $25 until 2026-10-10;
 * see DEEP_DISCOUNT for why it is $100.
 */
export const FULL_CREDIT_SAVINGS = 100;

/**
 * How much of the uplift each confidence level earns. A guess is worth
 * something -- it is still the only estimate we have -- but not full marks.
 */
export const CONFIDENCE_WEIGHT: Readonly<Record<PriceConfidence, number>> = Object.freeze({
  high: 1,
  medium: 0.8,
  low: 0.5,
});

/**
 * Estimate sources read as `low` whatever confidence they claim.
 *
 * `text` is the free estimate that rode on the tagging call: a title and no
 * comparables. It still claimed medium or high on 7,105 of 7,165 live listings
 * on prod, 2026-10-10, and put guesses like "comic lot worth $45" among the best
 * deals. Read as low it scores at most 50 and never shows the buyer callout.
 * The upkeep cron replaces it with Jev's comparables as it reaches each listing.
 */
export const LOW_CONFIDENCE_SOURCES: ReadonlySet<EstimateSource> = new Set<EstimateSource>(['text']);

/**
 * Estimate sources that never score. `fallback` is the last resort that prices
 * every new listing (utils/cron/listing-upkeep-engine.ts): looser neighbours,
 * or the category's median. Good enough to say the listing has a price; not
 * good enough to rank it as a deal.
 */
export const UNSCORED_SOURCES: ReadonlySet<EstimateSource> = new Set<EstimateSource>(['fallback']);

/** The confidence an estimate is scored at: its own, or `low` for a source that cannot earn more. */
export function effectiveConfidence(
  confidence: PriceConfidence | null | undefined,
  source: EstimateSource | null | undefined
): PriceConfidence | null {
  if (source && LOW_CONFIDENCE_SOURCES.has(source)) return 'low';
  return confidence ?? null;
}

/**
 * Widest the market band may be before the estimate stops meaning anything.
 *
 * `priceHigh / priceLow` above this is the model saying "somewhere between four
 * hundred and twelve hundred dollars", which is not a comp -- and a discount
 * measured against ANY number from an estimate that uncertain is noise, so this
 * vetoes the whole listing rather than only the midpoint fallback. Seen in the
 * wild on the 11 listings prod has analysed: a Versace bag came back 400-1200
 * (3.0x) while a Bose headset came back 350-500 (1.4x) and a houseboat
 * 220,000-350,000 (1.6x). The bar sits between them.
 */
export const MAX_BAND_RATIO = 2.5;

export interface DealScoreInput {
  /** What the seller is asking, in dollars. */
  price?: number | string | null;
  /** What comparable items actually sell for. Preferred reference. */
  marketPrice?: number | null;
  /** The model's fair-price estimate for this item. Reference when no comp exists. */
  suggestedPrice?: number | null;
  priceLow?: number | null;
  priceHigh?: number | null;
  confidence?: PriceConfidence | null;
  /** Which pass wrote the estimate. A source in LOW_CONFIDENCE_SOURCES scores as `low`. */
  estimateSource?: EstimateSource | null;
}

export interface DealScoreBreakdown {
  /** 0-100, where 0 means no measurable discount and 100 means DEEP_DISCOUNT or better. */
  score: number;
  /** The number the asking price was compared against. */
  reference: number | null;
  /** Fraction below the reference, before confidence damping. */
  discount: number;
  /** Dollars below the reference. */
  savings?: number;
  /** The confidence it was scored at, after LOW_CONFIDENCE_SOURCES. */
  confidence: PriceConfidence | null;
  /** Why the score is 0 when it is, for the dash and for tests. */
  reason?:
    | 'no price'
    | 'no reference'
    | 'band too wide'
    | 'at or above market'
    | 'fallback estimate'
    | 'scored';
}

const asNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * The reference price a discount is measured against.
 *
 * An explicit comp wins. Failing that the model's own fair-price estimate, and
 * failing that the midpoint of the band -- but only when the band is tight
 * enough to have a meaningful midpoint.
 */
export function referencePrice(input: DealScoreInput): number | null {
  const market = asNumber(input.marketPrice);
  if (market) return market;

  const suggested = asNumber(input.suggestedPrice);
  if (suggested) return suggested;

  const low = asNumber(input.priceLow);
  const high = asNumber(input.priceHigh);
  if (low && high && high >= low && high / low <= MAX_BAND_RATIO) return (low + high) / 2;

  return null;
}

/** Every part of the calculation, so the dash can show why a listing scored. */
export function dealScoreBreakdown(input: DealScoreInput): DealScoreBreakdown {
  const confidence = effectiveConfidence(input.confidence, input.estimateSource);
  const price = asNumber(input.price);
  if (!price) return { score: 0, reference: null, discount: 0, confidence, reason: 'no price' };
  if (input.estimateSource && UNSCORED_SOURCES.has(input.estimateSource)) {
    return { score: 0, reference: null, discount: 0, confidence, reason: 'fallback estimate' };
  }

  const low = asNumber(input.priceLow);
  const high = asNumber(input.priceHigh);
  if (low && high && high >= low && high / low > MAX_BAND_RATIO) {
    return { score: 0, reference: null, discount: 0, confidence, reason: 'band too wide' };
  }

  const reference = referencePrice(input);
  if (!reference) return { score: 0, reference: null, discount: 0, confidence, reason: 'no reference' };

  const discount = (reference - price) / reference;
  if (discount <= 0) {
    return { score: 0, reference, discount: 0, confidence, reason: 'at or above market' };
  }

  const weight = confidence ? CONFIDENCE_WEIGHT[confidence] : CONFIDENCE_WEIGHT.low;
  const savings = reference - price;
  const savingsWeight = Math.min(1, savings / FULL_CREDIT_SAVINGS);
  const raw = Math.min(1, discount / DEEP_DISCOUNT) * weight * savingsWeight;

  return {
    score: Math.round(Math.max(0, Math.min(1, raw)) * 100),
    reference,
    discount,
    savings,
    confidence,
    reason: 'scored',
  };
}

/** 0-100. Zero whenever there is nothing to say, never a penalty. */
export function calculateDealScore(input: DealScoreInput): number {
  return dealScoreBreakdown(input).score;
}

/**
 * Read the stored analysis off a listing row and score it.
 *
 * `aiPriceAnalysis` is free text as far as MySQL is concerned, written by two
 * different endpoints over several months, so it is parsed through the Zod
 * contract rather than trusted. Anything that fails the contract scores 0,
 * which is neutral -- a corrupt blob must not be able to move a listing.
 */
export function dealScoreFromAnalysis(
  analysis: string | null | undefined,
  price: number | string | null | undefined,
  dataSourceId?: number | null
): number {
  // `undefined` means the caller did not say, which keeps the older two-argument
  // callers working; an explicit external id scores 0.
  if (dataSourceId !== undefined && !dealScoreApplies(dataSourceId)) return 0;
  if (!analysis) return 0;

  let parsed: unknown;
  try {
    parsed = JSON.parse(analysis);
  } catch {
    return 0;
  }
  return dealScoreFromParsed(parsed, price);
}

/**
 * The same score from an analysis that is already an object -- the dash reads
 * the few fields it needs out of MySQL with JSON_EXTRACT rather than pulling
 * 300 bytes of text per row. Same contract, same zero for anything it fails.
 */
export function dealScoreFromParsed(
  parsed: unknown,
  price: number | string | null | undefined,
  dataSourceId?: number | null
): number {
  if (dataSourceId !== undefined && !dealScoreApplies(dataSourceId)) return 0;
  const result = AiPriceAnalysisSchema.safeParse(parsed);
  if (!result.success) return 0;
  const marketPrice = asNumber((parsed as { marketPrice?: unknown }).marketPrice);
  return calculateDealScore({
    price,
    marketPrice,
    suggestedPrice: result.data.suggestedPrice,
    priceLow: result.data.priceLow,
    priceHigh: result.data.priceHigh,
    confidence: result.data.confidence,
    estimateSource: result.data.estimateSource,
  });
}

/**
 * The whole breakdown from a stored analysis, for a screen that shows WHY a
 * listing scored what it did. Null when there is no estimate to score against,
 * or the source never gets a deal score -- both read as "no score", not 0.
 */
export function dealScoreDetailFromAnalysis(
  analysis: string | null | undefined,
  price: number | string | null | undefined,
  dataSourceId: number | null | undefined
): DealScoreBreakdown | null {
  if (!analysis || !dealScoreApplies(dataSourceId)) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(analysis);
  } catch {
    return null;
  }
  return dealScoreDetailFromParsed(parsed, price, dataSourceId);
}

/** As dealScoreDetailFromAnalysis, from an analysis already parsed (the product page has it as an object). */
export function dealScoreDetailFromParsed(
  parsed: unknown,
  price: number | string | null | undefined,
  dataSourceId: number | null | undefined
): DealScoreBreakdown | null {
  if (!dealScoreApplies(dataSourceId)) return null;
  const result = AiPriceAnalysisSchema.safeParse(parsed);
  if (!result.success) return null;
  return dealScoreBreakdown({
    price,
    marketPrice: asNumber((parsed as { marketPrice?: unknown }).marketPrice),
    suggestedPrice: result.data.suggestedPrice,
    priceLow: result.data.priceLow,
    priceHigh: result.data.priceHigh,
    confidence: result.data.confidence,
    estimateSource: result.data.estimateSource,
  });
}
